import type { TipoAlvoImpacto } from './alvosImpacto'
import type { DanoVeicular, NivelDanoVeicular, RegiaoDanoVeicular } from './danoVeicular'
import type { PerfilVeiculo } from './perfilVeiculo'

/**
 * Como a deformação daquela região se comporta.
 *
 * `impacto` é o caso geral (frente, traseira, lateral). Tombamento e
 * capotamento deformam o teto de maneiras diferentes: o tombamento para
 * sobre a lateral e amassa a borda do teto daquele lado; o capotamento passa
 * pelo teto e o rebaixa inteiro, mais do lado que tocou o solo primeiro.
 */
export type VarianteDeformacao = 'impacto' | 'tombamento' | 'capotamento'

/**
 * Descritor puro de deformação veicular.
 *
 * Determina como uma colisão afeta a geometria física/visual do veículo.
 * Permite seek para trás, reprodução, pausa e reset de forma 100% determinística,
 * sem acumular deslocamentos sobre vértices modificados.
 *
 * Um veículo pode ter VÁRIOS descritores (engavetamento: frente e traseira;
 * lateral que capota: porta e teto). Cada um carrega o `veiculoId` do veículo a
 * que pertence: o dano de A nunca é lido por B.
 */
export interface DescritorDeformacaoVeicular {
  veiculoId: string
  perfilId: string
  regiao: RegiaoDanoVeicular
  alvo: TipoAlvoImpacto
  /** Ponto central do impacto no referencial local do veículo */
  centroLocal: [number, number, number]
  larguraM: number
  intrusaoMaxM: number
  /** Instante de tempo normalizado da timeline (0..1) em que o impacto se inicia */
  instanteInicio: number
  /** Duração normalizada (0..1) do processo de deformação plástica */
  duracao: number
  /**
   * Centro do contato AO LONGO da face atingida, no referencial do veículo:
   * X na frente, na traseira e no teto; Z nas laterais. É o que permite impacto
   * deslocado, small overlap e porta dianteira × porta traseira × eixo.
   */
  centroFaceM?: number
  /** altura do centro de carga numa lateral (para-choque de quem atinge) */
  alturaContatoY?: number
  /**
   * Lado envolvido, em X do veículo (+1 ou −1): na lateral, a face atingida;
   * na frente e na traseira, o canto do small overlap; no teto, o lado que
   * encontra o solo primeiro.
   */
  sinalLateral?: 1 | -1
  smallOverlap?: boolean
  /**
   * −1..1: inclina o perfil de esmagamento para um lado. Contato entre dois
   * veículos raramente é perfeitamente alinhado; é daqui que vêm a torção do
   * capô e a diferença entre esquerda e direita.
   */
  assimetria?: number
  variante?: VarianteDeformacao
  nivel?: NivelDanoVeicular
  /** contato tangencial (abalroamento): a chapa ganha riscos ao longo do arrasto */
  riscos?: boolean
}

export interface AmostraDeformacao {
  progresso: number
  intrusaoAtualM: number
  deformado: boolean
}

/**
 * Amostra o estado da deformação em um instante de tempo específico da timeline.
 * Função pura e determinística.
 */
export function amostrarDeformacao(
  descritor: DescritorDeformacaoVeicular | null | undefined,
  tempo: number,
): AmostraDeformacao {
  if (!descritor) {
    return { progresso: 0, intrusaoAtualM: 0, deformado: false }
  }

  const { instanteInicio, duracao, intrusaoMaxM } = descritor
  if (duracao <= 0) {
    const ativo = tempo >= instanteInicio
    return {
      progresso: ativo ? 1 : 0,
      intrusaoAtualM: ativo ? intrusaoMaxM : 0,
      deformado: ativo && intrusaoMaxM > 0,
    }
  }

  const progressoLinear = (tempo - instanteInicio) / duracao
  const progresso = Math.max(0, Math.min(1, progressoLinear))
  // Curva de esmagamento progressivo (desaceleração com rigidez plástica crescente)
  const progressoSuave = Math.sin((progresso * Math.PI) / 2)
  const intrusaoAtualM = intrusaoMaxM * progressoSuave

  return {
    progresso: progressoSuave,
    intrusaoAtualM,
    deformado: progresso > 0 && intrusaoAtualM > 0.001,
  }
}

/**
 * Quanto da intrusão declarada pelo dano cada região aceita, e o teto dela.
 *
 * A frente tem a zona de absorção inteira para trabalhar; a lateral tem só a
 * porta; o teto é chapa sobre as colunas. Os limites são o "não exagerar":
 * mesmo o pior caso mantém a cabine reconhecível. Recurso de ensino, não
 * medida de resistência.
 */
const RESPOSTA_REGIAO: Record<RegiaoDanoVeicular, { fator: number; maximoM: number }> = {
  frente: { fator: 1, maximoM: 0.75 },
  traseira: { fator: 0.9, maximoM: 0.6 },
  lateralEsquerda: { fator: 0.72, maximoM: 0.42 },
  lateralDireita: { fator: 0.72, maximoM: 0.42 },
  teto: { fator: 0.55, maximoM: 0.3 },
}

/**
 * Face lateral atingida quando a cena não informa: esquerda (lado do
 * motorista) é +X, direita (passageiro) é −X — a mesma convenção de
 * `perfilVeiculo.ts`, das portas e da estrutura. No teto, o lado que desce.
 */
const sinalPadraoDaRegiao = (regiao: RegiaoDanoVeicular): 1 | -1 =>
  regiao === 'lateralDireita' ? -1 : 1

export interface OpcoesDescritorDeformacao {
  instanteInicio?: number
  duracao?: number
  veiculoId?: string
  centroFaceM?: number
  alturaContatoY?: number
  sinalLateral?: 1 | -1
  smallOverlap?: boolean
  assimetria?: number
  variante?: VarianteDeformacao
  riscos?: boolean
}

/**
 * Descritor para QUALQUER região. A frente continua com o mesmo resultado de
 * `criarDescritorDeformacaoFrontal`; traseira, laterais e teto passam a ter
 * descritor próprio em vez da casca genérica.
 */
export function criarDescritorDeformacao(
  perfil: PerfilVeiculo,
  dano: DanoVeicular,
  opcoes: OpcoesDescritorDeformacao = {},
): DescritorDeformacaoVeicular {
  const regiao = dano.regiao
  const resposta = RESPOSTA_REGIAO[regiao]
  const lateral = regiao === 'lateralEsquerda' || regiao === 'lateralDireita'
  const sinalLateral = opcoes.sinalLateral ?? sinalPadraoDaRegiao(regiao)
  const meiaLargura = perfil.largura / 2
  const smallOverlap = Boolean(opcoes.smallOverlap) && (regiao === 'frente' || regiao === 'traseira')

  // largura e centro ao longo da face
  let larguraM: number
  let centroFaceM: number
  if (lateral) {
    larguraM = Math.min(perfil.comprimento * 0.6, dano.larguraContatoM * 1.35)
    centroFaceM = opcoes.centroFaceM ?? perfil.ancoras[0]!.posicao.z
  } else if (regiao === 'teto') {
    larguraM = perfil.teto.zDianteiro - perfil.teto.zTraseiro
    centroFaceM = opcoes.centroFaceM ?? 0
  } else if (smallOverlap) {
    // 25% da largura, encostado no canto: a longarina fica de fora
    larguraM = perfil.largura * 0.3
    centroFaceM = opcoes.centroFaceM ?? sinalLateral * (meiaLargura - larguraM / 2)
  } else {
    larguraM = Math.min(perfil.largura - 0.04, dano.larguraContatoM)
    centroFaceM = opcoes.centroFaceM ?? 0
  }

  const alturaContatoY = opcoes.alturaContatoY ?? (dano.alvo === 'poste' ? perfil.cinturaY * 0.6 : 0.52)
  const centroLocal: [number, number, number] =
    regiao === 'frente'
      ? [centroFaceM, perfil.cinturaY * 0.62, perfil.frenteZ]
      : regiao === 'traseira'
        ? [centroFaceM, perfil.cinturaY * 0.62, perfil.traseiraZ]
        : lateral
          ? [sinalLateral * meiaLargura, alturaContatoY, centroFaceM]
          : [sinalLateral * meiaLargura * 0.4, perfil.tetoY, (perfil.teto.zDianteiro + perfil.teto.zTraseiro) / 2]

  // small overlap concentra a mesma energia numa área menor: mais fundo
  const fatorOverlap = smallOverlap ? 1.15 : 1
  const intrusaoMaxM = Math.min(resposta.maximoM, dano.intrusaoM * resposta.fator * fatorOverlap)

  return {
    veiculoId: opcoes.veiculoId ?? 'veiculoPrincipal',
    perfilId: perfil.id,
    regiao,
    alvo: dano.alvo,
    centroLocal,
    larguraM,
    intrusaoMaxM,
    instanteInicio: opcoes.instanteInicio ?? 0.22,
    duracao: opcoes.duracao ?? 0.28,
    centroFaceM,
    alturaContatoY,
    sinalLateral,
    smallOverlap,
    assimetria: Math.max(-1, Math.min(1, opcoes.assimetria ?? 0)),
    variante: opcoes.variante ?? 'impacto',
    nivel: dano.nivel,
    riscos: Boolean(opcoes.riscos),
  }
}

/**
 * Constrói o descritor de deformação para um impacto na região frontal.
 */
export function criarDescritorDeformacaoFrontal(
  perfil: PerfilVeiculo,
  dano: DanoVeicular,
  instanteInicio = 0.22,
  duracao = 0.28,
  veiculoId = 'veiculoPrincipal',
): DescritorDeformacaoVeicular {
  return criarDescritorDeformacao(perfil, { ...dano, regiao: 'frente' }, {
    instanteInicio,
    duracao,
    veiculoId,
  })
}
